import type { AntennaId } from '@wwf/sdk';

export type BaseInventoryByAntennaFilters = {
    rowSearch: string;
    antennaSearch: string;
    antennaTypes: string[];
    nonZeroStockOnly: boolean;
};

export const EMPTY_BASE_INVENTORY_BY_ANTENNA_FILTERS: BaseInventoryByAntennaFilters =
    {
        rowSearch: '',
        antennaSearch: '',
        antennaTypes: [],
        nonZeroStockOnly: false,
    };

export function normalizeInventorySearch(value: string) {
    return value.trim().toLowerCase();
}

export function matchesInventorySearch(
    query: string,
    ...fields: (string | null | undefined)[]
) {
    const normalizedQuery = normalizeInventorySearch(query);
    if (!normalizedQuery) {
        return true;
    }

    return fields.some(field => field?.toLowerCase().includes(normalizedQuery));
}

export function filterAntennasForInventory<
    T extends { name: string; type: string },
>(antennas: T[], filters: BaseInventoryByAntennaFilters) {
    return antennas.filter(antenna => {
        if (
            filters.antennaTypes.length > 0 &&
            !filters.antennaTypes.includes(antenna.type)
        ) {
            return false;
        }

        return matchesInventorySearch(filters.antennaSearch, antenna.name);
    });
}

export function filterRowsByNonZeroStock<
    TRow extends { id: string },
    TAntenna extends { id: AntennaId },
>({
    rows,
    antennas,
    nonZeroStockOnly,
    getCount,
}: {
    rows: TRow[];
    antennas: TAntenna[];
    nonZeroStockOnly: boolean;
    getCount: (row: TRow, antenna: TAntenna) => number | null;
}) {
    if (!nonZeroStockOnly) {
        return rows;
    }

    return rows.filter(row =>
        antennas.some(antenna => {
            const count = getCount(row, antenna);
            return count != null && count > 0;
        })
    );
}

export function hasActiveBaseInventoryFilters(
    filters: BaseInventoryByAntennaFilters
) {
    return (
        filters.rowSearch.trim().length > 0 ||
        filters.antennaSearch.trim().length > 0 ||
        filters.antennaTypes.length > 0 ||
        filters.nonZeroStockOnly
    );
}
